import { useState } from "react";
import { CaretDown, CaretUp, Plus, Wallet } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import {
  useBudgetBuckets,
  dispatchBudgetBucketsChanged,
} from "screens/investment/hooks/useBudgetBuckets";
import {
  BucketAmountSource,
  BudgetBucketView,
} from "screens/investment/types/budget-bucket";
import { BudgetBucketCard } from "./BudgetBucketCard";
import { BudgetBucketFormModal } from "./BudgetBucketFormModal";
import { BudgetBucketDrawer } from "./BudgetBucketDrawer";
import { BudgetSplitDesigner } from "screens/investment/components/BudgetSplitDesigner";

interface BudgetBucketsSectionProps {
  buildingId: string;
}

/** Korte bedragweergave voor de uitleg boven de budgetkaarten. */
const formatEuro = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/**
 * "Strategische budgetten" section that lives directly above the
 * Investeringslijnen section on the building's renovation tab.
 *
 * Shows a card per BudgetBucket with totals and approval state. Click a card
 * to open the detail drawer (full audit log + edit / approve / delete
 * actions). Investment lines pick from these pots via the bucket picker on
 * the items table (Phase 3).
 */
export const BudgetBucketsSection = ({
  buildingId,
}: BudgetBucketsSectionProps) => {
  const {
    buckets,
    isLoading,
    createBucket,
    updateBucket,
    setApproval,
    deleteBucket,
  } = useBudgetBuckets(buildingId);

  const [isExpanded, setIsExpanded] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [openBucket, setOpenBucket] = useState<BudgetBucketView | null>(null);
  // Bucket targeted by the split designer. Null = closed.
  const [splitBucketId, setSplitBucketId] = useState<number | null>(null);

  // Re-resolve the currently open bucket from the latest list whenever the
  // list refreshes — so toggling approval / editing inside the drawer picks
  // up the new computed totals without remounting.
  const drawerBucket = openBucket
    ? (buckets.find((b) => b.id === openBucket.id) ?? openBucket)
    : null;

  // Count of real (persisted) buckets, excluding the synthetic Overflow card
  // that the backend always appends.
  const echteBuckets = buckets.filter((b) => !b.isOverflow);
  const realBucketCount = echteBuckets.length;

  // Acquisitiebudget = de pot(ten) die het renovatiebudget bij aankoop van het
  // gebouw volgen. Dat is een keuze op de pot (`amountSource`), niet zijn naam.
  const acquisitieTotaal = echteBuckets
    .filter(
      (b) => b.amountSource === BucketAmountSource.ACQUISITION_RENOVATION_BUDGET,
    )
    .reduce((s, b) => s + (Number(b.totalAmount) || 0), 0);
  const andereTotaal = echteBuckets
    .filter(
      (b) => b.amountSource !== BucketAmountSource.ACQUISITION_RENOVATION_BUDGET,
    )
    .reduce((s, b) => s + (Number(b.totalAmount) || 0), 0);

  return (
    <div className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          {isExpanded ? (
            <CaretUp weight="bold" size={16} className="text-neutral-500" />
          ) : (
            <CaretDown weight="bold" size={16} className="text-neutral-500" />
          )}
          <Wallet weight="duotone" size={18} className="text-moss-primary" />
          <p className="text-lg font-semibold">Strategische budgetten</p>
          {/* Synthetic overflow card always exists, so the user-facing count
              only includes real, persisted pots. */}
          <span className="text-sm text-neutral-secondary-mid">
            ({realBucketCount} budget{realBucketCount === 1 ? "" : "ten"})
          </span>
        </button>

        <Button size="sm" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Nieuw strategisch budget
        </Button>
      </header>

      {isExpanded && (
        <>
          {/* De term uitgeschreven. "Zit het acquisitiebudget in het strategisch
              budget?" kwam telkens terug — het antwoord staat nu bij de potten
              zelf, met de bedragen erbij. */}
          {!isLoading && realBucketCount > 0 && (
            <p className="text-sm text-neutral-secondary-mid">
              Strategisch budget ={" "}
              <span className="font-medium text-neutral-primary">
                acquisitiebudget {formatEuro(acquisitieTotaal)}
              </span>{" "}
              +{" "}
              <span className="font-medium text-neutral-primary">
                andere strategische budgetten {formatEuro(andereTotaal)}
              </span>{" "}
              ={" "}
              <span className="font-semibold text-neutral-primary">
                {formatEuro(acquisitieTotaal + andereTotaal)}
              </span>
              {acquisitieTotaal === 0 &&
                " — dit gebouw heeft (nog) geen renovatiebudget bij aankoop staan"}
            </p>
          )}

          {isLoading && buckets.length === 0 ? (
            <div className="rounded-lg border border-neutral-200 bg-white p-6 text-center text-sm text-neutral-secondary-mid">
              Strategische budgetten laden...
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {buckets.map((bucket) => (
                <BudgetBucketCard
                  key={bucket.id}
                  bucket={bucket}
                  // Overflow is read-only: no drawer (no transactions to
                  // show), no approval workflow, no delete.
                  onClick={() => {
                    if (bucket.isOverflow) return;
                    setOpenBucket(bucket);
                  }}
                  onToggleApproval={() =>
                    setApproval(bucket.id, !bucket.isApproved)
                  }
                  // Split designer is the primary action on real (non-
                  // overflow) buckets — fans the pot out across labels.
                  onSplit={
                    bucket.isOverflow
                      ? undefined
                      : () => setSplitBucketId(bucket.id)
                  }
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Create modal */}
      <BudgetBucketFormModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={createBucket}
        buildingId={buildingId}
      />

      {/* Detail drawer */}
      <BudgetBucketDrawer
        bucket={drawerBucket}
        onClose={() => setOpenBucket(null)}
        onUpdate={updateBucket}
        onToggleApproval={setApproval}
        onDelete={deleteBucket}
      />

      {/* Budget detailverdeling designer — fast split of the chosen pot. */}
      <BudgetSplitDesigner
        bucket={
          splitBucketId
            ? (buckets.find((b) => b.id === splitBucketId) ?? null)
            : null
        }
        isOpen={splitBucketId !== null}
        onClose={() => setSplitBucketId(null)}
        onSaved={() => {
          // Fire the cross-component refresh event so this hook (and the
          // investment items table) reload with the freshly created lines
          // and the bucket's recomputed used/remaining totals.
          dispatchBudgetBucketsChanged(buildingId);
          setSplitBucketId(null);
        }}
      />
    </div>
  );
};
